import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Settings, Target, Briefcase, Bell, Database, AlertTriangle,
  Loader2, Shield, ChevronDown, ChevronRight,
  LayoutDashboard, Globe, Activity, BarChart3, ClipboardCheck, TrendingUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MODULE_ID_MAP = {
  "master-dashboard": "module_master_dashboard",
  "country-sentinel": "module_country_sentinel",
  "anomaly-detection": "module_anomaly_detection",
  "percentile-analysis": "module_percentile_analysis",
  "qa-validation": "module_qa_validation",
  "anomaly-detection-v2": "module_anomaly_detection_v2",
};

const MARKETING_SUBMODULES = [
  { key: "module_master_dashboard", title: "Master Dashboard", icon: LayoutDashboard, color: "text-amber-500" },
  { key: "module_campaign_performance", title: "Campaign Performance", icon: BarChart3, color: "text-blue-500" },
  { key: "module_country_sentinel", title: "Country Sentinel", icon: Globe, color: "text-blue-500" },
  { key: "module_anomaly_detection", title: "Detección de Anomalías (v1)", icon: Activity, color: "text-purple-500" },
  { key: "module_percentile_analysis", title: "Análisis de Percentiles", icon: BarChart3, color: "text-cyan-500" },
  { key: "module_qa_validation", title: "QA de Anuncios", icon: ClipboardCheck, color: "text-teal-500" },
  { key: "module_anomaly_detection_v2", title: "Anomalías v2 (Z-Score)", icon: TrendingUp, color: "text-orange-500" },
  { key: "module_percentile_analysis_v2", title: "Percentiles v2", icon: BarChart3, color: "text-cyan-500" },
  { key: "module_media_planner", title: "Media Planner", icon: TrendingUp, color: "text-violet-500" },
  { key: "module_campaign_structure", title: "Campaign Structure", icon: TrendingUp, color: "text-indigo-500" },
  { key: "module_budget_utilization", title: "Budget Utilization", icon: TrendingUp, color: "text-emerald-500" },
  { key: "module_performance_alerts", title: "Performance Alerts", icon: TrendingUp, color: "text-amber-500" },
  { key: "module_budget_monitor", title: "Monitor de Presupuesto", icon: TrendingUp, color: "text-slate-500" },
];

export const ModuleControl = () => {
  const [config, setConfig] = useState({
    notifications_enabled: true,
    marketing_enabled: true,
    operations_enabled: true,
    fivetran_enabled: true,
    module_master_dashboard: true,
    module_campaign_performance: true,
    module_country_sentinel: true,
    module_anomaly_detection: true,
    module_percentile_analysis: true,
    module_qa_validation: true,
    module_anomaly_detection_v2: true,
    module_percentile_analysis_v2: true,
    module_media_planner: true,
    module_campaign_structure: true,
    module_budget_utilization: true,
    module_performance_alerts: true,
    module_budget_monitor: false,
    query_log_visible: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/system-config`, { withCredentials: true });
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, value) => {
    setSaving(true);
    setConfig(prev => ({ ...prev, [key]: value }));

    try {
      // PATCH: only send the changed field
      await axios.patch(`${API}/admin/system-config`, { [key]: value }, { withCredentials: true });
      toast.success(`${value ? "Activado" : "Desactivado"} correctamente`);
      // Re-fetch full config from server
      const response = await axios.get(`${API}/admin/system-config`, { withCredentials: true });
      setConfig(prev => ({ ...prev, ...response.data }));
      window.dispatchEvent(new CustomEvent('systemConfigUpdated', { detail: response.data }));
    } catch (error) {
      toast.error("Error al guardar la configuración");
      setConfig(prev => ({ ...prev, [key]: !value }));
    } finally {
      setSaving(false);
    }
  };

  const topModules = [
    {
      key: "notifications_enabled",
      title: "Sistema de Notificaciones",
      description: "La campanita de notificaciones en tiempo real. Monitorea Fivetran, espacios privados y otros eventos.",
      icon: Bell, color: "text-blue-500", bgColor: "bg-blue-50",
      warning: "La campanita quedara oculta y no se ejecutaran queries de monitoreo."
    },
    {
      key: "marketing_enabled",
      title: "Modulo Marketing",
      description: "Categoria completa de Marketing.",
      icon: Target, color: "text-emerald-500", bgColor: "bg-emerald-50",
      warning: "Todos los modulos de Marketing quedaran inaccesibles.",
      disabled_control: true,
    },
    {
      key: "operations_enabled",
      title: "Modulo Operativos",
      description: "Workload Analysis y herramientas operativas del equipo.",
      icon: Briefcase, color: "text-orange-500", bgColor: "bg-orange-50",
      warning: "Los usuarios veran el modulo pero no podran acceder.",
      disabled_control: true,
    },
    {
      key: "fivetran_enabled",
      title: "Integracion Fivetran",
      description: "Monitor de conectores Fivetran en la seccion de Integraciones.",
      icon: Database, color: "text-purple-500", bgColor: "bg-purple-50",
      warning: "La opcion aparecera deshabilitada en Integraciones."
    },
    {
      key: "query_log_visible",
      title: "Query Log (Footer)",
      description: "Barra de Query Log visible en el footer para administradores.",
      icon: Database, color: "text-slate-500", bgColor: "bg-slate-50",
      warning: "El Query Log no sera visible en el footer."
    }
  ];

  const activeSubCount = MARKETING_SUBMODULES.filter(m => config[m.key]).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
          <Settings className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Control de Módulos</h2>
          <p className="text-sm text-slate-500">Activa o desactiva funcionalidades del sistema</p>
        </div>
      </div>

      <div className="grid gap-4">
        {topModules.map((module) => {
          const Icon = module.icon;
          const isEnabled = config[module.key];
          const isDisabledControl = module.disabled_control;

          return (
            <div key={module.key}>
              <Card className={`border transition-all ${isDisabledControl ? 'opacity-70 bg-slate-50' : !isEnabled ? 'opacity-60 bg-slate-50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${module.bgColor}`}>
                        <Icon className={`h-5 w-5 ${module.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{module.title}</h3>
                          {isDisabledControl ? (
                            <Badge className="bg-slate-200 text-slate-500">En mantenimiento</Badge>
                          ) : (
                            <Badge className={isEnabled ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}>
                              {isEnabled ? "Activo" : "Inactivo"}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{module.description}</p>
                        
                        {isDisabledControl && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 bg-slate-100 px-2 py-1.5 rounded">
                            <AlertTriangle className="h-3 w-3" />
                            Este control esta temporalmente deshabilitado. Los modulos visibles se gestionan directamente en el codigo.
                          </div>
                        )}

                        {!isDisabledControl && !isEnabled && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            <AlertTriangle className="h-3 w-3" />
                            {module.warning}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {!isDisabledControl && saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                      <Switch
                        checked={isDisabledControl ? true : isEnabled}
                        onCheckedChange={(checked) => !isDisabledControl && handleToggle(module.key, checked)}
                        disabled={isDisabledControl || saving}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800">Nota de Seguridad</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Los cambios afectan a todos los usuarios inmediatamente.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100 flex-shrink-0"
              onClick={async () => {
                if (!confirm("Esto reseteara la configuracion de modulos a los valores recomendados. ¿Continuar?")) return;
                try {
                  await axios.post(`${API}/admin/system-config/reset-modules`, {}, { withCredentials: true });
                  toast.success("Configuracion reseteada");
                  fetchConfig();
                } catch (e) { toast.error("Error al resetear"); }
              }}
            >
              Resetear Modulos
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
